import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, EyeOff, Search, Compass, Trophy, Quote } from "lucide-react";
import { LINKS, ASSETS } from "../lib/constants";
import NewsletterForm from "../components/site/NewsletterForm";

const SectionLabel = ({ children, dark = false, onYellow = false }) => (
  <span className={`section-label ${dark ? "on-dark" : ""} ${onYellow ? "on-yellow" : ""}`}>{children}</span>
);

const MethodStep = ({ num, title, body, icon: Icon }) => (
  <div className="card-soft p-7">
    <div className="flex items-center gap-3 mb-4">
      <span className="w-10 h-10 rounded-md bg-brand-yellow flex items-center justify-center border-[1.5px] border-brand-black">
        <Icon size={20} className="text-brand-black" strokeWidth={2.2} />
      </span>
      <span className="text-xs uppercase tracking-[0.18em] text-brand-stone font-bold">{num}</span>
    </div>
    <h3 className="font-display text-2xl mb-3 uppercase leading-tight">{title}</h3>
    <p className="text-sm leading-relaxed text-brand-stone">{body}</p>
  </div>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12 pt-12 pb-20 lg:pt-16 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 reveal">
            <SectionLabel>Profit &amp; Cashflow Strategy for Purpose-Led Business Owners</SectionLabel>

            <h1 className="font-display text-[clamp(2.4rem,5.6vw,4.6rem)] mt-6 leading-[0.95]">
              <span className="inline-block hi-yellow">Profitable but broke.</span>
              <span className="block mt-4 sm:mt-6">The quiet confession most business owners will never say out loud.</span>
            </h1>

            <p className="mt-7 text-lg leading-relaxed text-brand-ink max-w-2xl">
              You're making money. Clients are coming. The revenue looks healthy. But privately,
              you're checking your bank balance for reassurance, mentally adding up invoices in the
              shower, and wondering why more revenue hasn't created the calm you expected.
            </p>
            <p className="mt-5 text-lg leading-relaxed text-brand-ink max-w-2xl">
              You're not bad with money. You're <strong>Profitable But Broke</strong> — and there's
              a method for fixing it. It's called <strong>Follow The Cash™</strong>.
            </p>

            <div className="mt-9 flex flex-wrap gap-3" data-testid="hero-cta-group">
              <a href={LINKS.freeAssessment} target="_blank" rel="noopener noreferrer" className="btn-yellow" data-testid="hero-reality-check-btn">
                Get Your Free Reality Check <ArrowUpRight size={18} />
              </a>
              <Link to="/work-with-me" className="btn-secondary" data-testid="hero-work-with-me-btn">
                Explore ways to work with me
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] max-w-[460px] mx-auto">
              <div className="absolute inset-0 translate-x-4 translate-y-4 bg-brand-yellow rounded-[10px]" />
              <div className="absolute inset-0 -translate-x-2 -translate-y-2 bg-brand-black rounded-[10px] overflow-hidden border-[3px] border-brand-black">
                <img src={ASSETS.heroProfile} alt="Magdalena Mahdy — Profit and Cashflow Strategist, founder of ProfitMIND" className="w-full h-full object-cover" data-testid="hero-portrait" />
              </div>
              <div className="absolute -bottom-5 -left-5 bg-white border-[1.5px] border-brand-black rounded-md px-4 py-3 shadow-[4px_4px_0_#0A0A0A] rotate-[-3deg]">
                <p className="font-hand text-2xl leading-none text-brand-black">Hi, I'm Magdalena</p>
                <p className="text-[10px] uppercase tracking-[0.18em] text-brand-stone mt-1">Your Profit &amp; Cashflow Strategist</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-brand-black text-brand-yellow py-5 overflow-hidden border-y-[1.5px] border-brand-black">
          <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm uppercase tracking-[0.32em] font-bold text-center">
            <span>Stop Hiding</span>
            <span className="text-white/40">·</span>
            <span>Start Knowing</span>
            <span className="text-white/40">·</span>
            <span>Take Control</span>
            <span className="text-white/40">·</span>
            <span>Own Your Future</span>
          </div>
        </div>
      </section>

      {/* THE HONEST TRUTH */}
      <section className="bg-white py-20 lg:py-28" data-testid="honest-truth-section">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <SectionLabel>The honest truth</SectionLabel>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1] mt-6">
            You look successful on the outside. Inside, the money still feels{" "}
            <span className="hi-yellow">heavier than it should.</span>
          </h2>
          <p className="mt-10 text-lg leading-relaxed text-brand-ink">
            You have clients, sales, ideas, responsibilities and a bigger vision for where the
            business could go. But privately, you might still be:
          </p>
          <ul className="mt-6 space-y-3 text-lg leading-relaxed text-brand-ink list-disc pl-6 max-w-4xl">
            <li>Checking the bank balance for reassurance instead of reading a forecast</li>
            <li>Mentally adding up invoices and praying the timing works</li>
            <li>Delaying decisions because the numbers feel unclear</li>
            <li>Wondering why hitting revenue milestones hasn't created the calm you expected</li>
            <li>Doing your taxes and seeing profit on paper — but where has the cash gone?</li>
          </ul>
          <p className="mt-10 text-lg leading-relaxed text-brand-ink">
            This isn't because you're bad with money. It's because you're trying to make grown-up
            business decisions from scattered information, mental maths and whatever the bank
            balance says today.
          </p>
          <p className="mt-8 font-display text-2xl sm:text-3xl normal-case leading-[1.4] uppercase">
            At some point, "figuring it out as you go" becomes too expensive. Not just financially —
            emotionally, energetically and strategically too. That's where{" "}
            <span className="hi-yellow">Follow The Cash™</span> comes in.
          </p>
        </div>
      </section>

      {/* FOLLOW THE CASH METHOD */}
      <section className="bg-brand-yellow py-20 lg:py-28 border-y-[1.5px] border-brand-black" data-testid="method-section">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-6">
              <SectionLabel onYellow>The Follow The Cash™ Method</SectionLabel>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[0.95]">
                Four moves. One <span className="hi-white">calmer relationship</span> with your money.
              </h2>
            </div>
            <div className="lg:col-span-6 flex items-end">
              <p className="text-lg leading-relaxed text-brand-black max-w-2xl">
                Follow The Cash™ is a simple four-part method that moves you from avoidance and
                anxiety to evidence and calm. Not a rigid formula. Not a corporate finance lecture.
                A practical way to make your numbers useful — and stop being{" "}
                <strong>Profitable But Broke</strong>.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MethodStep icon={EyeOff} num="01" title="Stop Hiding" body="What are you avoiding, and what's it costing you? Face your numbers without shame. Reframe them as a tool, not a verdict. And definitely not a judgement." />
            <MethodStep icon={Search} num="02" title="Start Knowing" body="Why doesn't profit equal cash? What are the numbers really saying? Learn to read your financial picture in plain English — detective lessons, not accounting lectures." />
            <MethodStep icon={Compass} num="03" title="Take Control" body="How do you forecast without guessing? How do you make decisions with evidence? Build a 12-month cash map and turn it into a decision-making tool." />
            <MethodStep icon={Trophy} num="04" title="Own Your Future" body="How do you make it stick and plan for growth? Build financial rhythms, resilience and a business that supports your life — not one that quietly drains it." />
          </div>

          <div className="mt-10">
            <Link to="/follow-the-cash" className="btn-primary shadow-w-default" data-testid="explore-method-btn">
              Explore the full method <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* WAYS TO WORK WITH ME */}
      <section className="bg-white py-20 lg:py-28" data-testid="services-peek">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5 mb-12">
            <div>
              <SectionLabel>Ways To Work With Me</SectionLabel>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[0.95]">
                Multiple doorways in.<br />
                Start where it <span className="hi-yellow">hurts most.</span>
              </h2>
            </div>
            <Link to="/work-with-me" data-testid="services-peek-all-btn" className="btn-secondary self-start">
              See all options <ArrowUpRight size={18} />
            </Link>
          </div>

          <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-start">
            {/* Portrait */}
            <div className="lg:col-span-6">
              <div className="relative aspect-[4/5] w-full lg:sticky lg:top-32">
                <div className="absolute inset-0 translate-x-3 translate-y-3 bg-brand-yellow rounded-[10px]" />
                <div className="absolute inset-0 -translate-x-2 -translate-y-2 rounded-[10px] overflow-hidden border-[3px] border-brand-black">
                  <img src={ASSETS.waysToWorkPortrait} alt="Magdalena Mahdy" className="w-full h-full object-cover" data-testid="ways-to-work-portrait" />
                </div>
              </div>
            </div>

            {/* Stacked cards */}
            <div className="lg:col-span-6 space-y-5">
              <a href={LINKS.freeAssessment} target="_blank" rel="noopener noreferrer" className="card-soft p-7 group flex flex-col" data-testid="service-card-reality-check">
                <span className="text-xs uppercase tracking-[0.18em] text-brand-stone">01 — Free · 3 mins</span>
                <h3 className="font-display text-2xl mt-3 mb-2 leading-tight">The Profitable-But-Broke Reality Check</h3>
                <p className="text-sm leading-relaxed text-brand-stone">
                  A free reality check that tells you exactly where you sit on the Profitable-But-Broke
                  spectrum — and which part of Follow The Cash™ you need most right now.
                </p>
                <span className="inline-flex items-center gap-1 text-sm font-semibold mt-auto pt-5 group-hover:text-brand-yellow transition-colors">
                  Get Your Reality Check <ArrowUpRight size={15} />
                </span>
              </a>

              <a href={LINKS.forecastly} target="_blank" rel="noopener noreferrer" className="card-soft p-7 group flex flex-col" data-testid="service-card-forecastly">
                <span className="text-xs uppercase tracking-[0.18em] text-brand-stone">02 — Software</span>
                <h3 className="font-display text-2xl mt-3 mb-2 leading-tight">forecastly</h3>
                <p className="text-sm leading-relaxed text-brand-stone">
                  The 'Take Control' tool. A simple, guided cashflow forecasting tool for small
                  business owners who want their next 12 months in plain English. Built around
                  Follow The Cash™.
                </p>
                <span className="inline-flex items-center gap-1 text-sm font-semibold mt-auto pt-5 group-hover:text-brand-yellow transition-colors">
                  Visit forecastly <ArrowUpRight size={15} />
                </span>
              </a>

              <Link to={LINKS.moneyFlowSession} className="card-soft p-7 group flex flex-col bg-brand-yellow !border-brand-black" data-testid="service-card-moneyflow">
                <span className="text-xs uppercase tracking-[0.18em] text-brand-black/80 font-bold">03 — Strategy Session · 90 mins</span>
                <h3 className="font-display text-2xl mt-3 mb-2 leading-tight">Money Flow Strategy Session</h3>
                <p className="text-sm leading-relaxed text-brand-black">
                  Start Knowing, in action. A focused 90-minute session to understand what your
                  numbers are really telling you and what decision makes sense next.
                </p>
                <span className="inline-flex items-center gap-1 text-sm font-semibold mt-auto pt-5">
                  Book your session <ArrowUpRight size={15} />
                </span>
              </Link>

              <div data-testid="service-card-advisory" className="p-7 rounded-[10px] border-[1.5px] border-brand-black bg-white flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-[8px_8px_0_#0A0A0A]">
                <span className="text-xs uppercase tracking-[0.18em] text-brand-black/70 font-bold">04 — Advisory · 3–12 months</span>
                <h3 className="font-display text-2xl mt-3 mb-2 leading-tight">Profit &amp; Cashflow Advisory</h3>
                <p className="text-sm leading-relaxed text-brand-stone">
                  Own Your Future, ongoing. Private support for founders who want a calm, practical
                  and commercially-minded strategic partner beside them as they make bigger money
                  decisions.
                </p>
                <Link to={LINKS.financialLeadership} data-testid="service-card-advisory-cta" className="inline-flex items-center gap-1 text-sm font-semibold mt-auto pt-5 text-brand-black hover:text-brand-yellow transition-colors">
                  Explore advisory <ArrowUpRight size={15} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BUILT BY PROFITMIND — forecastly */}
      <section className="bg-brand-yellow py-20 border-y-[1.5px] border-brand-black" data-testid="forecastly-callout">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <SectionLabel onYellow>Built by ProfitMIND</SectionLabel>
              <h2 className="font-display text-4xl sm:text-5xl mt-6 leading-[1]">
                Meet <span className="hi-white">forecastly</span> —<br />
                your 'Take Control' tool.
              </h2>
              <div className="mt-6 space-y-5 text-lg leading-relaxed text-brand-black max-w-xl">
                <p>
                  forecastly is the cashflow forecasting tool I wish I had when I was running my
                  seasonal photography business. Forecasting helped me navigate quieter months,
                  plan ahead and make better decisions — but the spreadsheet itself was clunky,
                  manual and not exactly welcoming.
                </p>
                <p>
                  So I built a simpler version for small business owners who know cashflow matters
                  but don't want to wrestle with a complicated spreadsheet. Guided setup.
                  Plain-English insights. A calmer monthly rhythm with your money.
                </p>
                <p>
                  <strong>Built around the Follow The Cash™ method</strong> — so the tool and the
                  thinking are one and the same.
                </p>
              </div>
              <a href={LINKS.forecastly} target="_blank" rel="noopener noreferrer" className="btn-primary shadow-w-default mt-8" data-testid="forecastly-cta-btn">
                Visit forecastly <ArrowUpRight size={18} />
              </a>
            </div>
            <div>
              <a href={LINKS.forecastly} target="_blank" rel="noopener noreferrer" data-testid="forecastly-logo-link" className="block bg-white border-[1.5px] border-brand-black rounded-[10px] p-8 flex items-center justify-center min-h-[260px] transition-shadow duration-300 hover:shadow-[0_10px_24px_rgba(10,10,10,0.18)]" aria-label="Visit forecastly">
                <img src={ASSETS.forecastlyLogo} alt="forecastly by ProfitMIND" className="max-w-full max-h-[220px] object-contain" data-testid="forecastly-logo" />
              </a>
            </div>
          </div>

          <p className="mt-14 font-display text-2xl sm:text-3xl uppercase leading-[1.25] text-brand-black text-center">
            forecastly helps you see what's coming.<br className="hidden sm:block" />{" "}
            ProfitMIND helps you decide what to do next.
          </p>
        </div>
      </section>

      {/* FOUNDER STORY */}
      <section className="bg-white py-20 lg:py-28 border-t-[1.5px] border-brand-black/15" data-testid="trust-section">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <SectionLabel>The founder story: Meet Magdalena</SectionLabel>
            <Quote size={44} className="text-brand-yellow mt-4" strokeWidth={1.5} />
            <p className="font-display text-3xl sm:text-4xl leading-tight mt-3 mb-6">
              "I figured this out because I lived it too."
            </p>
            <div className="space-y-5 text-lg text-brand-ink leading-relaxed">
              <p>
                I have sat where many business owners sit: capable, creative, full of ideas, and
                still quietly aware that the money side needs more structure. Before ProfitMIND, I
                ran my own photography business for over a decade, where cashflow forecasting became
                one of the tools that helped me navigate seasonal income and make calmer decisions.
              </p>
              <p>
                I know what it feels like to be <strong>Profitable But Broke</strong> — invoicing
                well, but unable to sleep because the cash isn't there. Or looking at my tax bill
                and thinking, "where has all that money gone?!"
              </p>
              <p>
                Since then, I've spent nearly a decade mentoring founders and early-stage businesses
                through university partnerships, retrained in bookkeeping and tax, and built
                ProfitMIND to bring all of it together: practical numbers, business strategy and a
                very human understanding of why money can feel so heavy and loaded.
              </p>
              <p>
                My work isn't about turning you into a spreadsheet person. It's about helping you
                Follow The Cash™ — so you can stop being Profitable But Broke and start owning
                your future.
              </p>
            </div>
            <Link to="/about" className="btn-secondary mt-7" data-testid="home-read-my-story-btn">
              Read my story <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] max-w-[440px] mx-auto lg:mx-0 lg:ml-auto">
              <div className="absolute inset-0 translate-x-3 translate-y-3 bg-brand-yellow rounded-[10px]" />
              <div className="absolute inset-0 -translate-x-1 -translate-y-1 rounded-[10px] overflow-hidden border-[3px] border-brand-black">
                <img src={ASSETS.founderStory} alt="Magdalena Mahdy working outdoors with laptop and coffee" className="w-full h-full object-cover" data-testid="trust-portrait" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <NewsletterForm />

      {/* FINAL CTA */}
      <section className="bg-brand-yellow border-t-[1.5px] border-brand-black" data-testid="home-final-cta">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12 py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <h3 className="font-display text-3xl sm:text-4xl uppercase max-w-3xl leading-tight text-brand-black">
            Ready to stop being Profitable But Broke?
          </h3>
          <div className="flex flex-wrap gap-3 shrink-0">
            <a href={LINKS.freeAssessment} target="_blank" rel="noopener noreferrer" data-testid="home-cta-reality-check-btn" className="btn-primary shadow-w-default">
              Get your free Reality Check <ArrowUpRight size={18} />
            </a>
            <a href={LINKS.tidycal} target="_blank" rel="noopener noreferrer" data-testid="home-cta-book-btn" className="btn-secondary">
              Book a free 20-min chat
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
